/**
 * Rent-ins on a job: third-party equipment on rent for this job
 * (POST /jobs/:id/rent-ins, PATCH / DELETE /rent-ins/:id, fieldiq-api#1338).
 *
 * Read by dispatch and logistics (WL_RENT_IN_READ); written by WL_DISPATCH.
 * The rent status, days on rent and logged hours are the API's. The cost rate
 * appears only when the API sent it (the caller may see cost).
 */
import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAddWlRentIn, useRemoveWlRentIn } from '../../hooks/useWlJobs';
import { fmtWlNumber, wlBlankToNull } from '../../lib/wireline-slickline';
import { wlDay, wlHours, wlJobRefusal, wlRentStatusSkin } from '../../lib/wireline-slickline-jobs';
import type { WlCatalog } from '../../types/wireline-slickline';
import type { WlJobDetail } from '../../types/wireline-slickline-jobs';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WL_SMALL_BTN, WlBadge, WlDialog, WlEmpty, WlField, WlFormError, WlPanel } from './WlBits';

export function JobRentInsPanel({ job, catalog, canWrite, open }: { job: WlJobDetail; catalog: WlCatalog | null; canWrite: boolean; open: boolean }) {
  const add = useAddWlRentIn();
  const remove = useRemoveWlRentIn();
  const rentIns = job.rentIns ?? [];
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [f, setF] = useState({ vendor: '', description: '', unitNumber: '', on: '', off: '', rate: '', rateUnit: '', billable: true, po: '' });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((s) => ({ ...s, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  return (
    <WlPanel
      eyebrow="Rent-ins"
      title="Rented-in equipment"
      testId="wl-job-rent-ins"
      actions={
        canWrite && open ? (
          <button type="button" className={WL_SMALL_BTN} onClick={() => { setError(null); setAdding(true); }}>
            <Plus className="size-3.5" aria-hidden="true" />
            Add rent-in
          </button>
        ) : undefined
      }
    >
      <WlFormError message={rowError} />
      {rentIns.length === 0 ? (
        <WlEmpty>Nothing is rented in for this job.</WlEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rentIns.map((r) => (
            <li key={r.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3" data-testid="wl-rent-in">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-extrabold text-white">{r.description}</p>
                  <WlBadge skin={wlRentStatusSkin(r.rentStatus)} value={r.rentStatus} />
                </div>
                <p className="mt-0.5 text-xs text-fieldiq-muted">
                  {r.vendorName}
                  {r.unitNumber ? ` · ${r.unitNumber}` : ''} · {wlDay(r.onRentOn)} – {wlDay(r.offRentOn)}
                </p>
                <p className="mt-1 text-xs text-fieldiq-text">
                  {r.onRentDays === null || r.onRentDays === undefined ? '—' : `${fmtWlNumber(r.onRentDays)} day${r.onRentDays === 1 ? '' : 's'}`} on rent · {wlHours(r.loggedHours)} logged
                  {r.costRate !== undefined ? ` · ${r.costRate === null ? '—' : fmtWlNumber(r.costRate)}${r.rateUnitLabel ? ` / ${r.rateUnitLabel}` : ''}` : ''}
                </p>
              </div>
              {canWrite && open && (
                <button
                  type="button"
                  className={WL_SMALL_BTN}
                  aria-label={`Remove rent-in ${r.description}`}
                  disabled={remove.isPending}
                  onClick={() => {
                    setRowError(null);
                    remove.mutate(r.id, {
                      onSuccess: () => toast.success('Rent-in removed'),
                      onError: (err) => setRowError(wlJobRefusal(err, 'The rent-in could not be removed.')),
                    });
                  }}
                >
                  <Trash2 className="size-3.5" aria-hidden="true" />
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <WlDialog
        open={adding}
        title="Add a rent-in"
        onClose={() => setAdding(false)}
        footer={
          <>
            <button type="button" className={WL_SECONDARY} onClick={() => setAdding(false)} disabled={add.isPending}>
              Cancel
            </button>
            <button
              type="button"
              className={WL_PRIMARY}
              disabled={add.isPending}
              onClick={() => {
                setError(null);
                if (!f.vendor.trim() || !f.description.trim() || !f.on) return setError('The vendor, a description and the on-rent day are needed.');
                const rate = f.rate.trim() === '' ? null : Number(f.rate);
                if (rate !== null && !Number.isFinite(rate)) return setError('The rate must be a number.');
                add.mutate(
                  {
                    jobId: job.id,
                    body: {
                      vendor_name: f.vendor.trim(),
                      description: f.description.trim(),
                      unit_number: wlBlankToNull(f.unitNumber),
                      on_rent_on: f.on,
                      off_rent_on: f.off || null,
                      cost_rate: rate,
                      rate_unit: f.rateUnit || null,
                      billable: f.billable,
                      po_number: wlBlankToNull(f.po),
                    },
                  },
                  {
                    onSuccess: () => {
                      toast.success('Rent-in added');
                      setAdding(false);
                    },
                    onError: (err) => setError(wlJobRefusal(err, 'The rent-in could not be added.')),
                  },
                );
              }}
            >
              {add.isPending ? 'Adding…' : 'Add rent-in'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <WlFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2">
            <WlField label="Vendor">
              <input className={WL_INPUT} aria-label="Vendor" value={f.vendor} onChange={set('vendor')} />
            </WlField>
            <WlField label="Description">
              <input className={WL_INPUT} aria-label="Description" value={f.description} onChange={set('description')} />
            </WlField>
            <WlField label="Vendor unit number">
              <input className={WL_INPUT} aria-label="Vendor unit number" value={f.unitNumber} onChange={set('unitNumber')} />
            </WlField>
            <WlField label="PO number">
              <input className={WL_INPUT} aria-label="Rent-in PO number" value={f.po} onChange={set('po')} />
            </WlField>
            <WlField label="On rent">
              <input type="date" className={WL_INPUT} aria-label="On rent" value={f.on} onChange={set('on')} />
            </WlField>
            <WlField label="Off rent" hint="Blank: still on rent">
              <input type="date" className={WL_INPUT} aria-label="Off rent" value={f.off} onChange={set('off')} />
            </WlField>
            <WlField label="Cost rate">
              <input inputMode="decimal" className={WL_INPUT} aria-label="Cost rate" value={f.rate} onChange={set('rate')} />
            </WlField>
            <WlField label="Per">
              <select className={WL_INPUT} aria-label="Rate unit" value={f.rateUnit} onChange={set('rateUnit')}>
                <option value="">—</option>
                {(catalog?.rateUnits ?? []).map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </WlField>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-fieldiq-text">
            <input type="checkbox" checked={f.billable} onChange={set('billable')} />
            Billable to the customer
          </label>
        </div>
      </WlDialog>
    </WlPanel>
  );
}
